<template>
  <div class="footer__wrapper w-full flex justify-center bg-black-90 overflow-x-hidden mt-10">
    <footer class="footer w-full pt-6 px-4 sm:pt-0 sm:pb-0 sm:px-0 font-medium">
      <LazyHydrate never>
        <div class="border-b border-white-10 w-full">
          <div class="sm:w-[1100px] justify-start flex mx-auto sm:pt-12 sm:pb-4 flex-wrap content-between">
            <div class="sm:w-[250px] mt-4 sm:mt-0 sm:h-full sm:mr-6 mr-0">
              <div class="text-[#C7C8CB] text-xl sm:mb-4 mb-2">{{ $t('footer.recently-added') }}</div>
              <ul class="text-[#A9AAAD] flex flex-col text-13 leading-18">
                <li v-for="article in recentArticles" :key="article._id" class="footer__article">
                  <OLink :href="getArticleUrl({ article })" v-bind="bind(article)">{{ article.title }}</OLink>
                </li>
              </ul>
            </div>
            <div class="sm:w-[250px] mt-6 sm:mt-0 sm:h-full sm:mr-6 mr-0">
              <div class="text-[#C7C8CB] text-xl sm:mb-4 mb-2">{{ $t('footer.popular-headlines-of-the-day') }}</div>
              <ul class="text-[#A9AAAD] flex flex-col text-13 leading-18">
                <li v-for="article in popularArticles" :key="article._id" class="footer__article">
                  <OLink :href="getArticleUrl({ article })" v-bind="bind(article)">{{ article.title }}</OLink>
                </li>
              </ul>
            </div>
            <div class="sm:w-[550px] order-2 sm:order-none mt-4 sm:mt-0 mb-6 sm:mb-0 sm:h-full">
              <div class="text-[#C7C8CB] text-xl sm:mb-4 mb-2">{{ $t('footer.fast-access') }}</div>
              <ul class="text-[#A9AAAD] text-13 leading-18 block sm:column-2 column-1">
                <li v-for="article in quickAccessArticles" :key="article._id" class="footer__article line-clamp-2 min-h-6">
                  <OLink :href="getArticleUrl({ article })" v-bind="bind(article)">{{ article.title }}</OLink>
                </li>
              </ul>
            </div>
          </div>
          <div class="sm:w-[1100px] mx-auto sm:mb-7 mb-4">
            <div class="text-[#C7C8CB] text-xl sm:mb-4 mb-2">{{ $t('footer.discover') }}</div>
            <div class="flex flex-wrap -mx-2">
              <OLink
                v-for="tag in footerTags"
                :key="tag._id"
                :title="tag.name"
                class="text-[#A9AAAD] py-1 px-2 rounded-sm bg-[#3B3C40] block text-13 mx-2 mb-2"
                :href="tag.route"
              >
                {{ tag.name }}
              </OLink>
            </div>
          </div>
        </div>
      </LazyHydrate>
      <div class="border-b border-white-10 w-full py-6">
        <div class="sm:w-[1100px] flex sm:flex-row flex-col sm:justify-center sm:items-center sm:mx-auto">
          <ul
            class="
              flex
              sm:flex-row
              flex-col flex-wrap
              sm:flex-nowrap sm:space-x-[5px]
              text-[#A9AAAD] text-13
              leading-24
              w-full
              h-[155px]
              sm:h-auto
            "
          >
            <li v-if="IS_ONEDIO">
              <OLink :href="`/${CORPORATE_PATH}/${ABOUT_PATH}`" :aria-label="$t('corporate.about-us')" :title="$t('corporate.about-us')">
                {{ $t('corporate.about-us') }}
              </OLink>
            </li>
            <li>
              <div
                :aria-label="$t('footer.feedback')"
                class="cursor-pointer hover:text-o-blue"
                :title="$t('footer.feedback')"
                @click="handleFeedbackModal"
              >
                {{ $t('footer.feedback') }}
              </div>
            </li>
            <li v-if="IS_ONEDIO || IS_CO">
              <OLink
                :href="`/${CORPORATE_PATH}/${PRIVACY_POLICY_PATH}`"
                :aria-label="$t('corporate.privacy-policy')"
                :title="$t('corporate.privacy-policy')"
              >
                {{ $t('corporate.privacy-policy') }}
              </OLink>
            </li>
            <li v-if="IS_ONEDIO">
              <OLink
                :href="`/${CORPORATE_PATH}/${COMMUNITY_RULES_PATH}`"
                :aria-label="$t('corporate.community-rules')"
                :title="$t('corporate.community-rules')"
              >
                {{ $t('corporate.community-rules') }}
              </OLink>
            </li>
            <li>
              <OLink
                rel="nofollow"
                target="_blank"
                href="https://kobiozel.onedio.com/"
                :aria-label="$t('footer.advert')"
                :title="$t('footer.advert')"
              >
                {{ $t('footer.advert') }}
              </OLink>
            </li>
            <li v-if="IS_ONEDIO">
              <OLink :href="`/${CORPORATE_PATH}/${CONTACT_PATH}`" :aria-label="$t('corporate.contact')" :title="$t('corporate.contact')">
                {{ $t('corporate.contact') }}
              </OLink>
            </li>
            <li>
              <OLink :href="`/${CORPORATE_PATH}/${CAREER_PATH}`" :aria-label="$t('corporate.career')" :title="$t('corporate.career')">
                {{ $t('corporate.career') }}
              </OLink>
            </li>
            <li v-if="IS_ONEDIO">
              <OLink
                :href="`/${CORPORATE_PATH}/${USER_AGREEMENT_PATH}`"
                :aria-label="$t('corporate.user-agreement')"
                :title="$t('corporate.user-agreement')"
              >
                {{ $t('corporate.user-agreement') }}
              </OLink>
            </li>
            <li v-if="IS_ONEDIO">
              <OLink
                :href="`/${CORPORATE_PATH}/${PUBLICATION_PRINCIPLES_PATH}`"
                :aria-label="$t('corporate.publication-principles')"
                :title="$t('corporate.publication-principles')"
              >
                {{ $t('corporate.publication-principles') }}
              </OLink>
            </li>
            <li v-if="IS_ONEDIO">
              <OLink :href="`/${CORPORATE_PATH}/${MASTHEAD_PATH}`" :aria-label="$t('corporate.masthead')" :title="$t('corporate.masthead')">
                {{ $t('corporate.masthead') }}
              </OLink>
            </li>

            <li>
              <OLink href="/rss" aria-label="RSS" title="RSS">RSS</OLink>
            </li>
          </ul>
          <ul class="flex mt-6 sm:mt-0">
            <a
              v-for="item in socialMedias"
              :key="item.name"
              :href="item.link"
              class="mr-2"
              target="_blank"
              rel="noopener noreferrer nofollow"
              :title="item.name"
            >
              <li
                class="
                  w-[34px]
                  h-[34px]
                  border
                  rounded-full
                  border-white-20
                  flex
                  items-center
                  justify-center
                  hover:bg-white-20 hover:text-white
                "
              >
                <ORawIcon v-if="item.icon === 'instagram'" :icon="`article/${item.icon}`" svg-class="w-5 h-5 text-[#7F8083]" />
                <ORawIcon v-else :icon="`article/${item.icon}`" svg-class="w-4 h-4 text-[#7F8083]" />
              </li>
            </a>
          </ul>
        </div>
      </div>
      <div class="py-6 sm:w-[1100px] mx-auto flex sm:flex-row flex-col sm:items-center sm:justify-between">
        <div class="flex sm:items-center flex-col sm:flex-row pt-6 sm:pt-0">
          <a href="/" aria-label="onedio logo" class="mr-6 order-last sm:order-none flex items-center">
            <OIcon icon="onedio-white" svg-class="h-6 w-20" />
          </a>
          <span class="text-[#A9AAAD] text-13 leading-24 mb-4 sm:mb-0">© {{ year }} Onedio. {{ $t('footer.all-rights-reserved') }}.</span>
        </div>
        <div v-if="IS_ONEDIO" class="flex sm:order-none order-first pb-6 sm:pb-0 border-b border-white-10 sm:border-none">
          <a rel="nofollow noopener" target="_blank" :href="APP_LINK" aria-label="apple-store" title="Apple Store">
            <OImage loading="lazy" src="/images/app-store.svg" alt="apple-store" class="mr-2 h-9" />
          </a>
          <a rel="nofollow noopener" target="_blank" :href="APP_LINK" aria-label="play-store" title="Play Store">
            <OImage loading="lazy" src="/images/google-play.svg" alt="google-play" class="mr-2 h-9" />
          </a>
          <a rel="nofollow noopener" target="_blank" :href="APP_LINK" aria-label="app-gallery" title="App Gallery">
            <OImage loading="lazy" src="/images/app-gallery.svg" alt="app-gallery" class="mr-2 h-9" />
          </a>
        </div>
      </div>
      <div class="media-footer flex items-start justify-center sm:items-center mx-auto flex-col border-t border-white-10 w-full py-4">
        <div class="flex justify-center items-center text-white text-base leading-24 font-normal gap-2 mb-6">
          Bir
          <a href="https://mediazonehq.com/?utm_source=onedio&utm_medium=footer" target="_blank">
            <OIcon icon="mediazone-logo" svg-class="h-8 w-[142px]" />
          </a>
          markasıdır.
        </div>
        <div class="media-group flex sm:justify-center justify-start items-center flex-wrap sm:gap-6 gap-3">
          <a class="group" href="https://www.onedio.com/?utm_source=onedio&utm_medium=footer" target="_blank">
            <OIcon icon="onedio-grey" class="block group-hover:hidden" />
            <OIcon icon="onedio-white" class="hidden group-hover:block" />
          </a>
          <div class="border-r border-white-10 h-9"></div>
          <a class="group" href="https://www.mackolik.com/canli-sonuclar?utm_source=onedio&utm_medium=footer" target="_blank">
            <OIcon icon="mackolik-grey" class="block group-hover:hidden" />
            <OIcon icon="mackolik-white" class="hidden group-hover:block" />
          </a>
          <div class="border-r border-white-10 h-9"></div>
          <a class="group" href="https://www.matchendirect.fr/?gbp=1&utm_source=onedio&utm_medium=footer" target="_blank">
            <OIcon icon="matchendirect-grey" class="block group-hover:hidden" />
            <OIcon icon="matchendirect-white" class="hidden group-hover:block" />
          </a>
          <div class="hidden sm:block border-r border-white-10 h-9"></div>
          <a class="group" href="https://www.mynet.com/?utm_source=onedio&utm_medium=footer" target="_blank">
            <OIcon icon="mynet-grey" class="block group-hover:hidden" />
            <OIcon icon="mynet-white" class="hidden group-hover:block" />
          </a>

          <div class="border-r border-white-10 h-9"></div>
          <a class="group" href="https://www.webtekno.com/?utm_source=onedio&utm_medium=footer" target="_blank">
            <OIcon icon="webtekno-grey" class="block group-hover:hidden" />
            <OIcon icon="webtekno-white" class="hidden group-hover:block" />
          </a>
          <div class="border-r border-white-10 h-9"></div>
          <a class="group" href="https://yemek.com/?utm_source=onedio&utm_medium=footer" target="_blank">
            <OIcon icon="yemekcom-grey" class="block group-hover:hidden" />
            <OIcon icon="yemekcom-white" class="hidden group-hover:block" />
          </a>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { defineComponent, useContext, useFetch, ref } from '@nuxtjs/composition-api';
import LazyHydrate from 'vue-lazy-hydration';
import { socialMediaAccounts } from '~/modules/common/components/app/header/headerUtils';
import { getArticleUrl } from '~/modules/common/utils/routingUtils';
import { useModal } from '~/composables/useModal';
import { OLink, OImage, OIcon, ORawIcon } from '~/modules/common/components/ui';

import {
  CORPORATE_PATH,
  ABOUT_PATH,
  CONTACT_PATH,
  CAREER_PATH,
  MASTHEAD_PATH,
  COOKIE_POLICY_PATH,
  PRIVACY_POLICY_PATH,
  USER_AGREEMENT_PATH,
  PUBLICATION_PRINCIPLES_PATH,
  COMMUNITY_RULES_PATH,
  IS_ONEDIO,
  IS_CO,
  APP_LINK,
} from '~/constants/config';

export default defineComponent({
  name: 'Footer',
  serverCacheKey() {
    // Will change every 30 secondes
    return 'Footer@30';
  },
  components: { LazyHydrate, OLink, OImage, OIcon, ORawIcon },
  setup() {
    const { $service } = useContext();
    const popularArticles = ref([]);
    const recentArticles = ref([]);
    const footerTags = ref([]);
    const quickAccessArticles = ref([]);
    const socialMedias = socialMediaAccounts.filter((social) => social.name !== 'Whatsapp');
    const { openModal } = useModal();
    const handleFeedbackModal = () => {
      openModal(import('~/modules/common/components/modals/ModalFeedback' /* webpackChunkName: "ModalFeedback" */));
    };
    useFetch(async () => {
      try {
        const megaFooterData = await $service.article.getFooterData();
        popularArticles.value = megaFooterData?.data?.popularMegaFooterArticles;
        recentArticles.value = megaFooterData?.data?.recentMegaFooterArticles;
        footerTags.value = megaFooterData?.data?.footerHashtags;
        quickAccessArticles.value = megaFooterData?.data?.quickAccessArticles;
      } catch {}
    });
    const bind = (article) => {
      const params = {};
      params.title = article.title;
      if (article?.redirectUrl) {
        params.rel = 'nofollow';
      }
      return params;
    };
    const year = new Date().getFullYear();

    return {
      socialMedias,
      popularArticles,
      recentArticles,
      getArticleUrl,
      quickAccessArticles,
      handleFeedbackModal,
      footerTags,
      bind,
      CORPORATE_PATH,
      ABOUT_PATH,
      CONTACT_PATH,
      CAREER_PATH,
      MASTHEAD_PATH,
      COOKIE_POLICY_PATH,
      PRIVACY_POLICY_PATH,
      USER_AGREEMENT_PATH,
      PUBLICATION_PRINCIPLES_PATH,
      COMMUNITY_RULES_PATH,
      IS_ONEDIO,
      IS_CO,
      APP_LINK,
      year,
    };
  },
});
</script>
<style lang="scss" scoped>
.footer__article {
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.footer__category {
  margin-bottom: 0.5rem;
}

@media (min-width: 430px) {
  .media-group {
    gap: 16px;
  }
}

@media (min-width: 640px) {
  .footer__article {
    margin-bottom: 1rem;
  }

  .footer__category {
    margin-bottom: 0.5rem;
  }
}
</style>
